import type { VariantProps } from 'class-variance-authority';
import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  ElementType,
  ReactNode,
  Ref,
} from 'react';

import { cnva } from '@/app/utils/cnva';

export const validSectionComponents = [
  'div',
  'article',
  'aside',
  'nav',
  'section',
  'header',
  'footer',
] satisfies ElementType[];

export type SectionAs = (typeof validSectionComponents)[number];

const SectionStyled = cnva('relative w-full', {
  variants: {
    size: {
      sm: 'py-4 md:py-6 xl:py-12',
      md: 'py-7 md:py-10 xl:py-16',
      lg: 'py-10 md:py-14 xl:py-20',
      none: '',
    },
    overflow: {
      hidden: 'overflow-hidden',
      none: '',
    },
  },
  defaultVariants: {
    size: 'lg',
    overflow: 'hidden',
  },
});

export type SectionProps<T extends SectionAs = 'section'> = Omit<
  ComponentPropsWithoutRef<T>,
  'children' | 'as' // 1. Omit 'as' to prevent conflicts
> &
  VariantProps<typeof SectionStyled> & {
    /**
     * Specifies the HTML tag for the Section component.
     */
    as?: T; // 2. Bind this to the generic T, not the whole union
    /**
     * The content to be displayed within the section.
     */
    children: ReactNode;
    /**
     * Additional CSS class names to apply to the section.
     */
    className?: string;
    /**
     * A React ref to the Section component.
     */
    ref?: Ref<ComponentRef<T>>; // 3. Get the actual DOM element type
  };

export const Section = <T extends SectionAs = 'section'>({
  as,
  children,
  className,
  size = 'lg',
  overflow = 'hidden',
  ref,
  ...rest
}: SectionProps<T>) => {
  const CustomComponent = (
    as && validSectionComponents.includes(as as SectionAs) ? as : 'section'
  ) as any;

  return (
    <CustomComponent
      ref={ref}
      className={SectionStyled({ size, overflow, className })}
      {...rest}
    >
      {children} {/* 5. Actually render the children! */}
    </CustomComponent>
  );
};
